import { AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  formatMismatchFieldLine,
  getDisplayableMismatchFields,
  getIdentityMismatchSeverity,
} from '@/lib/identityMismatch';
import type { IdentityMismatch } from '@/types';

interface Props {
  mismatch: IdentityMismatch;
  className?: string;
}

export default function IdentityMismatchPanel({ mismatch, className }: Props) {
  const { t } = useLanguage();
  const displayFields = getDisplayableMismatchFields(mismatch.fields);
  const severity = getIdentityMismatchSeverity(mismatch);

  if (displayFields.length === 0 || !severity) return null;

  const isCritical = severity === 'critical';
  const Icon = isCritical ? AlertTriangle : Info;

  return (
    <Card
      className={cn(
        isCritical
          ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-blue-500/30 bg-blue-500/5',
        className,
      )}
    >
      <CardContent className="min-w-0 space-y-3 px-3 py-4 sm:px-4 md:px-6">
        <div className="flex min-w-0 items-start gap-2">
          <Icon
            className={cn(
              'h-4 w-4 shrink-0 mt-0.5',
              isCritical
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-blue-600 dark:text-blue-400',
            )}
          />
          <div className="space-y-1 min-w-0">
            <p
              className={cn(
                'text-sm font-medium',
                isCritical
                  ? 'text-amber-800 dark:text-amber-200'
                  : 'text-blue-900 dark:text-blue-100',
              )}
            >
              {t(
                isCritical
                  ? 'application.identityMismatch.panelTitle'
                  : 'application.identityMismatch.panelTitleContact',
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(
                isCritical
                  ? 'application.identityMismatch.panelIntro'
                  : 'application.identityMismatch.panelIntroContact',
              )}
            </p>
          </div>
        </div>

        <ul className="min-w-0 space-y-1.5 pl-0 text-sm sm:pl-6">
          {displayFields.map((field) => (
            <li key={field.field} className="break-words text-foreground">
              {formatMismatchFieldLine(field, t)}
            </li>
          ))}
        </ul>

        <p className="break-words pl-0 text-xs text-muted-foreground sm:pl-6">
          {t('application.identityMismatch.panelFooter')}
        </p>
      </CardContent>
    </Card>
  );
}
